import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { Switch } from '@mui/material';
import { Collapse } from '@mui/material';
import borderedFieldStyles from '../MessageEditor/styles.module.scss';
import { SimpleText } from 'util/IntlMessages';
import {
  DIVERSION_POLICIES,
  DIVERSION_YAML_SEPARATOR,
  NOTICE_BODY_MAX_LENGTH,
} from 'constants/Values';
import ShelterListManager from './ShelterListManager';
import { composeDiversionBody, diversionBodyBudget } from './diversionBody';

const valueShape = PropTypes.shape({
  preamble: PropTypes.string.isRequired,
  shelters: PropTypes.array.isRequired, // shelterShape — shape duplicated in ShelterListManager
  policy: PropTypes.oneOf([
    DIVERSION_POLICIES.NONE,
    DIVERSION_POLICIES.NEAREST,
    DIVERSION_POLICIES.FASTEST,
  ]).isRequired,
  cancel: PropTypes.bool.isRequired,
});

class DiversionSubform extends Component {
  static propTypes = {
    value: valueShape.isRequired,
    onChange: PropTypes.func.isRequired,
    // Threaded into serializeBody, and read by render since the counter has to price the
    // cancellation payload too (Path A carries a notificationId, which costs characters).
    notificationId: PropTypes.string,
    // Parent-supplied validation errors. Populated by NotificationForm's
    // handleSend on submit-block (task 06). Defaulted to {} so the prop is
    // always-present for safe destructure / spread. Mirrors the
    // `<Filter validationMessage />` precedent at NotificationForm.jsx:736 +
    // Filter.jsx:31,244-249 (parent owns validation, child renders inline).
    validationErrors: PropTypes.shape({
      preamble: PropTypes.string,
      shelters: PropTypes.string,
    }),
  };

  static defaultProps = {
    notificationId: undefined,
    validationErrors: {},
  };

  // Default initial state for parent to spread into its own state.
  static defaultValue = {
    preamble: '',
    shelters: [],
    policy: DIVERSION_POLICIES.NEAREST,
    cancel: false,
  };

  /**
   * Serialise the subform's value object into the body field that POST
   * /api/v1/core/notifications/filters expects. Format is:
   *
   *     <preamble><DIVERSION_YAML_SEPARATOR>\n<yamlText>
   *
   * which gives the server's parser (DiversionPayloadParser) the literal
   * '\n---' separator at body.indexOf('\n---'), then yamlText starting at
   * '---' (the YAML doc-start marker that SnakeYAML happily ignores).
   *
   * For cancel: emits { cancel: true } (Path B) or { cancel: true,
   * notificationId: <id> } (Path A — banner-prefilled). Optional shelter
   * fields with `undefined` values are explicitly stripped before safeDump.
   *
   * @param {Object} value - { preamble, shelters, policy, cancel }
   * @param {string|undefined} notificationId - Path A threading from task 09.
   * @returns {string} The composed body string.
   */
  static serializeBody(value, notificationId) {
    return composeDiversionBody(value, notificationId);
  }

  /**
   * Validate the subform's value. Returns { ok, errors } where errors is a
   * map of field-name → error-key (i18n-resolvable string id).
   *
   * Rules:
   *  - preamble must not contain DIVERSION_YAML_SEPARATOR ('\n---'). Server
   *    splits on first occurrence; a later occurrence in the preamble would
   *    be eaten by the YAML parser and corrupt the payload silently.
   *  - When cancel === false, shelters.length must be >= 1.
   *
   * @returns {{ ok: boolean, errors: { preamble?: string, shelters?: string } }}
   */
  static validate(value) {
    const errors = {};
    if (value.preamble && value.preamble.indexOf(DIVERSION_YAML_SEPARATOR) >= 0) {
      // Inline literal — no dedicated i18n key today (see Ambiguity #2). Task
      // 06 may upgrade this to an i18n key resolution; for now task 06 reads
      // errors.preamble as a presence-flag and renders its own copy.
      errors.preamble = 'PREAMBLE_CONTAINS_SEPARATOR';
    }
    if (!value.cancel && (!value.shelters || value.shelters.length === 0)) {
      errors.shelters = 'AT_LEAST_ONE_SHELTER_REQUIRED';
    }
    return { ok: Object.keys(errors).length === 0, errors };
  }

  // Internal-only state — preserves the shelters list when the cancel toggle
  // is switched on so it can be restored when toggled off again. This is the
  // "preserved-not-destroyed" mechanism per spec D2.
  state = {
    lastShelters: [],
  };

  handlePreambleChange = event => {
    const { value, onChange } = this.props;
    onChange({ ...value, preamble: event.target.value });
  };

  handleSheltersChange = nextShelters => {
    const { value, onChange } = this.props;
    onChange({ ...value, shelters: nextShelters });
  };

  handleCancelToggle = (event, checked) => {
    const { value, onChange } = this.props;
    if (checked) {
      // Going off -> on: stash the current shelters so we can restore them
      // when the user toggles back off. Empty the live array so a stale
      // cancel-with-shelters state never reaches the YAML serialiser.
      this.setState({ lastShelters: value.shelters });
      onChange({ ...value, cancel: true, shelters: [] });
    } else {
      // Going on -> off: restore the previously-stashed shelters.
      const restored = this.state.lastShelters;
      onChange({ ...value, cancel: false, shelters: restored });
    }
  };

  handlePolicyChange = (event, nextPolicy) => {
    const { value, onChange } = this.props;
    onChange({ ...value, policy: nextPolicy });
  };

  renderPreambleError() {
    const { value, validationErrors } = this.props;
    // Two trigger paths:
    //  (1) Render-time visual cue: preamble contains '\n---' as the user types.
    //  (2) Parent-supplied submit-block error from validationErrors.preamble.
    // Either path renders the same element. Both use data-testid for tests.
    const liveTriggered = value.preamble && value.preamble.indexOf(DIVERSION_YAML_SEPARATOR) >= 0;
    const parentTriggered = !!validationErrors.preamble;
    if (!liveTriggered && !parentTriggered) return null;
    return (
      <Box
        sx={{ color: 'error.main', mt: 0.5, fontSize: '80%', fontWeight: 400 }}
        data-testid="preamble-separator-error"
      >
        Preamble cannot contain &apos;---&apos; on its own line — please rephrase.
      </Box>
    );
  }

  renderSheltersError() {
    const { validationErrors } = this.props;
    // Only triggered by parent's submit-block (live render-time validation
    // would be noisy — TM hasn't tried to send yet). Cleared by NotificationForm
    // on the next handleDiversionChange call.
    if (!validationErrors.shelters) return null;
    return (
      <Box
        sx={{ color: 'error.main', mt: 0.5, fontSize: '80%', fontWeight: 400 }}
        data-testid="shelters-required-error"
      >
        <SimpleText id="diversion.composer.shelters.min" />
      </Box>
    );
  }

  render() {
    const { value, notificationId } = this.props;
    const { preamble, shelters, policy, cancel } = value;
    // The safe zones travel INSIDE this body, so the operator's allowance depends on how many
    // they have added — see `diversionBody.js` for the measured cost and the resulting ceiling.
    const budget = diversionBodyBudget(value, notificationId);

    return (
      <Box
        className="diversion-subform"
        sx={{ borderTop: 1, borderColor: 'divider', pt: 2, mt: 2 }}
      >
        <FormGroup row sx={{ pb: 2 }}>
          <div>
            <FormControlLabel
              control={
                <Switch
                  checked={cancel}
                  onChange={this.handleCancelToggle}
                  color="primary"
                  slotProps={{ input: { 'data-testid': 'cancel-toggle' } }}
                />
              }
              label={<SimpleText id="diversion.composer.cancel-toggle" />}
            />
            <div
              className={borderedFieldStyles.helperDescription}
              style={{ marginLeft: 48, marginTop: -4 }}
            >
              <SimpleText id="diversion.composer.cancel-toggle.helper" />
            </div>
          </div>
        </FormGroup>

        <FormGroup row sx={{ pb: 2 }}>
          <div className={`diversion-preamble-wrapper ${borderedFieldStyles.borderedField}`}>
            <TextField
              id="diversion-preamble"
              label={
                <SimpleText
                  id={cancel ? 'diversion.composer.title.cancel' : 'diversion.composer.title'}
                />
              }
              multiline
              rows="3"
              fullWidth
              value={preamble}
              onChange={this.handlePreambleChange}
              error={budget.over}
              helperText={
                <React.Fragment>
                  <SimpleText
                    id={
                      cancel
                        ? 'diversion.composer.preamble.placeholder.cancel'
                        : 'diversion.composer.preamble.placeholder'
                    }
                  />
                  {/* The safe zones are IN this body, so the count moves when the operator
                      adds one without touching the text. Showing the composed total rather
                      than the preamble's own length is the only way that reads honestly. */}
                  <span
                    data-testid="diversion-preamble-counter"
                    style={{ float: 'right', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {budget.composed}/{NOTICE_BODY_MAX_LENGTH}
                  </span>
                </React.Fragment>
              }
              slotProps={{ htmlInput: { 'data-testid': 'diversion-preamble' } }}
            />
          </div>
          {this.renderPreambleError()}
        </FormGroup>

        {/* Shelter list + policy collapse together when cancel toggle flips ON
            so the morph is a smooth height/opacity transition rather than an
            instant DOM removal. */}
        <Collapse in={!cancel} mountOnEnter>
          <ShelterListManager value={shelters} onChange={this.handleSheltersChange} />
          {this.renderSheltersError()}
          <FormGroup row sx={{ pb: 2, mt: 2 }}>
            <FormLabel component="legend">
              <SimpleText id="diversion.composer.policy.label" />
            </FormLabel>
            <RadioGroup
              value={policy}
              onChange={this.handlePolicyChange}
              data-testid="policy-radio-group"
            >
              <FormControlLabel
                value={DIVERSION_POLICIES.NEAREST}
                control={<Radio color="primary" />}
                label={<SimpleText id="diversion.composer.policy.nearest" />}
              />
              <FormControlLabel
                value={DIVERSION_POLICIES.FASTEST}
                control={<Radio color="primary" />}
                label={<SimpleText id="diversion.composer.policy.fastest" />}
              />
              <FormControlLabel
                value={DIVERSION_POLICIES.NONE}
                control={<Radio color="primary" />}
                label={<SimpleText id="diversion.composer.policy.none" />}
              />
            </RadioGroup>
          </FormGroup>
        </Collapse>
      </Box>
    );
  }
}

export default DiversionSubform;
